// Stock counts (migrations 070, 071; Mitch, 2026-11-04: "an automated system which shows as one of the tabs on the
// inventory area, and as an item on the home page dashboard"): today's short list of SKUs to count by hand (shared/counts.ts
// ranks them, the server makes it once a day), each with its rank and why it's on it; admins can pick again, take a SKU off
// (the next best takes its place) or add one. A count is just the number, taken as it is: whoever counts never sees the books'
// number or whether it matched (Mitch: "I do not want to incentivize fudging count numbers … more or less just accept
// numbers"). Admins alone get the Report: each count against what the books said then and what they say now, where a
// difference is fixed by clicking the stock number, like anywhere else.
import { Check, ClipboardCheck, MessageSquare, RefreshCw, Settings2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { numberOf } from "../../../../shared/calc.ts";
import { COUNT_REASON_LABELS, COUNT_RULES, type CountReason, countWhy } from "../../../../shared/counts.ts";
import { planToday } from "../../../../shared/day.ts";
import type { CountTask, Item, StockCount } from "../../../../shared/db.gen.ts";
import { dayText, plural, qty } from "../../lib/format.ts";
import { can, mutate, mutateWithResult } from "../../lib/mutate.ts";
import { navigate, usePath } from "../../lib/router.tsx";
import { me } from "../../lib/session.ts";
import { useRows } from "../../lib/sync.ts";
import { Badge, Button, cx, Input, Thumb } from "../../ui/controls.tsx";
import { Popover } from "../../ui/Popover.tsx";
import { PaneLink } from "../../ui/SidePane.tsx";
import { ItemPicker } from "../consignment/parts.tsx";
import { CountsReport } from "./CountsReport.tsx";

const asked = new Set<string>(); // days this browser has asked the server to make (once each)

/** Today's list (made by the server the first time anyone who counts opens Stock counts or Today that day). */
export function useCountDay() {
  const today = planToday();
  const day = useRows("count_day").find((d) => d.day === today);
  const tasks = useRows("count_task");
  useEffect(() => {
    if (day || asked.has(today) || !can("count.make_day")) return;
    asked.add(today);
    mutate("count.make_day", {});
  }, [day, today]);
  const mine = useMemo(() => tasks.filter((t) => t.day === today && !t.dropped).sort((a, b) => a.position - b.position), [tasks, today]);
  const done = mine.filter((t) => t.count_id).length;
  const units = mine.reduce((n, t) => n + Math.max(0, Number(t.on_hand)), 0);
  return { today, made: !!day, tasks: mine, done, left: mine.length - done, units };
}

const isAdmin = () => me()?.role === "admin";
const itemPage = (i: Item) => (i.kind === "sell" ? `/items/sell?open=${i.id}` : `/parts?open=${i.id}`);
const REASON_TONE: Record<CountReason, "warn" | "accent" | "neutral" | "ok"> = {
  running_low: "warn",
  never: "accent",
  due: "neutral",
  picked: "ok",
};
const timeText = (at: string) => new Date(at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

export function CountsPage() {
  const path = usePath();
  const admin = isAdmin();
  // The Report has its own address, so its table's views start from no filters (a ?tab= would read as one).
  const tab = admin && path === "/counts/report" ? "report" : "today";
  const day = useCountDay();
  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex h-12 shrink-0 items-center gap-3 border-b border-line bg-surface px-4">
        <h1 className="text-[15px] font-semibold whitespace-nowrap">Stock counts</h1>
        {admin && (
          <nav className="flex items-center gap-1">
            {(["today", "report"] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => navigate(t === "today" ? "/counts" : "/counts/report")}
                className={cx("rounded-md px-2.5 py-1 text-[13px]", tab === t ? "bg-surface-2 font-medium" : "text-muted hover:bg-hover hover:text-text")}
              >
                {t === "today" ? "Today" : "Report"}
              </button>
            ))}
          </nav>
        )}
        <span className="ml-auto" />
        {admin && <DaySize />}
      </header>
      {tab === "today" ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto max-w-5xl px-6 py-5">
            <TodayTab day={day} />
          </div>
        </div>
      ) : (
        <div className="min-h-0 flex-1">
          <CountsReport />
        </div>
      )}
    </div>
  );
}

// Today --------------------------------------------------------------------------------------------------------------

function TodayTab({ day }: { day: ReturnType<typeof useCountDay> }) {
  const items = useRows("item");
  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const counts = useRows("stock_count");
  const countById = useMemo(() => new Map(counts.map((c) => [c.id, c])), [counts]);
  const hhl = useRows("location").find((l) => l.kind === "hhl" && l.active && !l.ignored);
  const [extra, setExtra] = useState<{ key: string; itemId: string; locationId: string }[]>([]);
  const pct = day.tasks.length ? Math.round((day.done / day.tasks.length) * 100) : 0;
  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg border border-line bg-surface p-4">
        <div className="flex items-baseline gap-3">
          <span className="text-[15px] font-semibold">{dayText(day.today)}</span>
          <span className="text-muted">
            {!day.made
              ? "Making today's list…"
              : day.tasks.length
                ? `${plural(day.tasks.length, "SKU")} · ${day.left ? `${day.left} left` : "all counted, thanks"}`
                : "Nothing to count today"}
          </span>
        </div>
        {day.tasks.length > 0 && (
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-2">
            <div className="h-full rounded-full bg-ok transition-all" style={{ width: `${pct}%` }} />
          </div>
        )}
        <p className="mt-2 text-[12px] text-muted">
          Count every unit of each on the shelf and enter the number you count. That's all: it's taken as it is. Add a note if something's odd (some in another
          spot, damaged ones).
        </p>
        <p className="mt-1 text-[12px] text-faint">
          Picked in order of how much they matter: the best sellers first, a boost for ones running low (quick to count before they're reordered), and slower
          sellers when they're long overdue (top sellers every {Math.round(COUNT_RULES.topEveryDays / 30)} months, the rest once a year). Never a SKU at zero,
          and nothing counted in the last {COUNT_RULES.minGapDays} days.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {can("count.add") ? (
          <>
            <ItemPicker
              className="w-80"
              placeholder="Add a SKU to today's list…"
              allow={(i) => i.kind !== "service"}
              onPick={(i) => mutate("count.add", { item_id: i.id })}
            />
            <Button
              size="sm"
              icon={<RefreshCw className="size-3.5" />}
              disabled={!day.left}
              title="Every SKU not counted yet is replaced by the next best (they're left out today)"
              onClick={() => mutate("count.pick_again", {})}
            >
              Pick again
            </Button>
          </>
        ) : (
          can("stock_count.record") &&
          hhl && (
            <ItemPicker
              className="w-96"
              placeholder="Count something else: scan or type a SKU…"
              allow={(i) => i.kind !== "service"}
              onPick={(i) => setExtra((xs) => [{ key: crypto.randomUUID(), itemId: i.id, locationId: hhl.id }, ...xs])}
            />
          )
        )}
      </div>

      <div className="flex flex-col gap-2">
        {extra.map((x) => (
          <CountRow key={x.key} item={itemById.get(x.itemId)} locationId={x.locationId} />
        ))}
        {day.tasks.map((t, i) => (
          <CountRow
            key={t.id}
            rank={i + 1}
            item={itemById.get(t.item_id)}
            locationId={t.location_id}
            task={t}
            done={t.count_id ? countById.get(t.count_id) : undefined}
          />
        ))}
      </div>
    </div>
  );
}

function CountRow({ item, locationId, task, done, rank }: { item?: Item; locationId: string; task?: CountTask; done?: StockCount; rank?: number }) {
  const users = useRows("app_user");
  const [text, setText] = useState("");
  const [note, setNote] = useState<string | null>(null); // null: no note box open
  const [saving, setSaving] = useState(false);
  const [mine, setMine] = useState<{ counted: number; note: string; at: string } | null>(null); // a count off the list
  const [recount, setRecount] = useState(false);
  if (!item) return null;
  const counted = done ? { counted: Number(done.counted), note: done.note, at: done.counted_at, by: done.counted_by } : mine ? { ...mine, by: me()?.id } : null;
  const counting = can("stock_count.record") && (!counted || recount);

  const save = async () => {
    const n = numberOf(text);
    if (!text.trim() || !Number.isInteger(n) || n < 0) return;
    setSaving(true);
    const { ok } = await mutateWithResult("stock_count.record", {
      id: crypto.randomUUID(),
      item_id: item.id,
      location_id: locationId,
      counted: n,
      note: (note ?? "").trim(),
      ...(task ? { task_id: task.id } : {}),
    });
    setSaving(false);
    if (!ok) return;
    if (!task) setMine({ counted: n, note: (note ?? "").trim(), at: new Date().toISOString() });
    setText("");
    setNote(null);
    setRecount(false);
  };

  const by = counted?.by ? users.find((u) => u.id === counted.by)?.name?.split(" ")[0] : "";
  return (
    <div className="rounded-lg border border-line bg-surface">
      <div className="flex items-center gap-3 px-3 py-2">
        {rank !== undefined && <span className="w-6 shrink-0 text-right font-mono text-[13px] font-semibold text-faint">{rank}</span>}
        <Thumb src={item.photo_url} size={32} />
        <div className="min-w-40 flex-1">
          <div className="flex min-w-0 items-center gap-2 whitespace-nowrap">
            <PaneLink kind="item" id={item.id} to={itemPage(item)} className="shrink-0 font-mono text-[12px] font-medium hover:underline">
              {item.sku}
            </PaneLink>
            {task ? <Badge tone={REASON_TONE[task.reason as CountReason]}>{COUNT_REASON_LABELS[task.reason as CountReason]}</Badge> : <Badge>Extra</Badge>}
            <span className="truncate text-[12px] text-muted">{item.name}</span>
          </div>
          {task && (
            <div className="truncate text-[12px] text-faint">
              {countWhy(task, { pickedBy: users.find((u) => u.id === task.picked_by)?.name?.split(" ")[0], day: (d) => dayText(d) })}
            </div>
          )}
        </div>
        {counted && !recount && (
          <div className="flex min-w-0 shrink items-center gap-2 text-[12px] whitespace-nowrap">
            <Check className="size-4 shrink-0 text-ok" />
            <span>
              Counted <span className="font-mono font-semibold">{qty(counted.counted)}</span>
            </span>
            {counted.note && (
              <span className="min-w-0 max-w-48 truncate text-muted" title={counted.note}>
                {counted.note}
              </span>
            )}
            <span className="text-faint">
              {by ? `${by} · ` : ""}
              {timeText(counted.at)}
            </span>
            {task && can("stock_count.record") && (
              <button type="button" onClick={() => setRecount(true)} className="text-faint underline-offset-2 hover:text-text hover:underline">
                Recount
              </button>
            )}
          </div>
        )}
        {counting && (
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              title="Add a note"
              onClick={() => setNote((n) => (n === null ? "" : null))}
              className={cx("rounded p-1.5 hover:bg-hover", note !== null ? "text-accent" : "text-faint hover:text-text")}
            >
              <MessageSquare className="size-4" />
            </button>
            <Input
              aria-label={`${item.sku} counted`}
              autoFocus={recount}
              value={text}
              inputMode="numeric"
              placeholder="Counted"
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                if (e.key === "Escape" && recount) setRecount(false);
              }}
              className="h-8 w-24 text-right font-mono"
            />
            <Button size="sm" variant="primary" disabled={!text.trim() || saving} onClick={save}>
              Save
            </Button>
            {task && !counted && can("count.drop") && (
              <button
                type="button"
                title="Take it off today's list (the next one takes its place)"
                onClick={() => mutate("count.drop", { task_id: task.id })}
                className="rounded p-1.5 text-faint hover:bg-hover hover:text-text"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
        )}
      </div>
      {note !== null && counting && (
        <div className="border-t border-line px-3 py-2">
          <Input
            autoFocus
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && save()}
            placeholder="A note with the count (e.g. 6 more in the back room, 2 damaged)"
            className="h-8 text-[12px]"
          />
        </div>
      )}
    </div>
  );
}

// How big a day is (admins) -------------------------------------------------------------------------------------------

function DaySize() {
  const settings = useRows("planning_settings")[0];
  if (!settings) return null;
  const editable = can("planning_settings.update");
  const field = (key: "count_units_per_day" | "count_skus_per_day" | "count_skip_over", label: string, hint: string) => (
    <label className="flex flex-col gap-1 text-[12px] text-muted">
      {label}
      <Input
        defaultValue={String(settings[key])}
        disabled={!editable}
        inputMode="numeric"
        onBlur={(e) => {
          const n = numberOf(e.target.value);
          if (Number.isInteger(n) && n >= 1 && n !== settings[key]) mutate("planning_settings.update", { id: settings.id, patch: { [key]: n } });
        }}
        className="h-8 font-mono"
      />
      <span className="text-[11px] text-faint">{hint}</span>
    </label>
  );
  return (
    <Popover
      width={320}
      button={(toggle) => (
        <Button size="sm" variant="ghost" icon={<Settings2 className="size-3.5" />} onClick={toggle}>
          {qty(settings.count_units_per_day)} units a day
        </Button>
      )}
    >
      {() => (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2 font-medium">
            <ClipboardCheck className="size-4 text-muted" /> How big a day's list is
          </div>
          {field("count_units_per_day", "Units a day, about", "The list fills up to this many units")}
          {field("count_skus_per_day", "SKUs a day, at most", "However few units they have")}
          {field("count_skip_over", "Leave out SKUs with more than", "Too many to count by hand: never on a list")}
          <p className="text-[11px] text-faint">
            Looks off first, then running low, then due: top sellers about every {Math.round(COUNT_RULES.topEveryDays / 30)} months, the rest once a year. A
            change shapes tomorrow's list; today's is made.
          </p>
        </div>
      )}
    </Popover>
  );
}
